import React, { useMemo, useState, ChangeEvent, useCallback, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { CreditCard, Search, Wallet, Loader2, RefreshCw } from "lucide-react";
import { toast } from "react-toastify";
import PageHeader from "../../components/ui/PageHeader";
import { BetaBadge } from "../../components/ui/BetaBadge";
import { Button } from "../../components/ui/Button";
import { useAuth } from "../../auth/AuthContext";
import type { PaymentReceivedRow } from "../../context/paymentsTypes";
import { hasXonderPaymentSetup } from "../../lib/paymentsXonder";
import {
  fetchPayments,
  getPaymentsApiErrorMessage,
  mapApiPaymentToRow,
} from "../../services/paymentsApi";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";

function formatMoney(amount: number, currency: string): string {
  try {
    return new Intl.NumberFormat("en-GB", { style: "currency", currency }).format(amount);
  } catch {
    return `${currency} ${amount.toFixed(2)}`;
  }
}

/** Backend sends `Y-m-d H:i:s` (e.g. 2025-03-15 14:30:00). */
function formatPaymentDate(value: string): string {
  if (!value || value === "—") return "—";
  const isoLike = value.includes("T") ? value : value.replace(" ", "T");
  const d = new Date(isoLike);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function PaymentsSetupEmpty({ onSetup }: { onSetup: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center min-h-[min(480px,60vh)] px-6 py-12 text-center">
      <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-purple-50 text-[#885ABB] mb-5">
        <Wallet className="h-8 w-8" />
      </div>
      <h2 className="text-xl font-semibold text-gray-900">Set up payments to receive money</h2>
      <p className="mt-2 max-w-md text-sm text-gray-600 leading-relaxed">
        Your Xonder payment account is not connected yet. Complete payment setup to start
        receiving customer payments in this list.
      </p>
      <Button
        type="button"
        className="mt-6 bg-[#885ABB] hover:bg-[#6f47a2] text-white px-8"
        onClick={onSetup}
      >
        Set up payment details
      </Button>
    </div>
  );
}

function PaymentsNoResultsEmpty() {
  return (
    <div className="flex flex-col items-center justify-center h-64 text-gray-400 w-full">
      <Search className="w-12 h-12 mb-3 opacity-20" />
      <p className="text-lg font-medium text-gray-500">No payments found</p>
      <p className="text-sm">Try adjusting your search</p>
    </div>
  );
}

function PaymentsReceivedEmpty() {
  return (
    <div className="flex flex-col items-center justify-center min-h-[280px] px-6 py-10 text-center">
      <CreditCard className="h-12 w-12 text-gray-300 mb-4" />
      <p className="text-lg font-medium text-gray-600">No payments received yet</p>
    </div>
  );
}

const PaymentsListPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const needsSetup = !hasXonderPaymentSetup(user);

  const [payments, setPayments] = useState<PaymentReceivedRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);

  const [search, setSearch] = useState("");
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const fetchGenRef = useRef(0);

  const loadPayments = useCallback(
    async (opts?: { startingAfter?: string; append?: boolean }) => {
      if (needsSetup) return;

      const gen = ++fetchGenRef.current;
      const append = Boolean(opts?.append);

      if (append) {
        setLoadingMore(true);
      } else {
        setLoading(true);
        setLoadError(null);
      }

      try {
        const { payments: rows, hasMore: more, nextCursor: cursor } = await fetchPayments({
          limit: Math.min(100, Math.max(1, itemsPerPage)),
          ...(opts?.startingAfter ? { starting_after: opts.startingAfter } : {}),
          ...(dateFrom ? { from: dateFrom } : {}),
          ...(dateTo ? { to: dateTo } : {}),
        });

        if (gen !== fetchGenRef.current) return;

        const mapped = rows.map(mapApiPaymentToRow);
        setPayments((prev) => (append ? [...prev, ...mapped] : mapped));
        setHasMore(more);
        setNextCursor(cursor);
      } catch (err: unknown) {
        if (gen !== fetchGenRef.current) return;
        const message = getPaymentsApiErrorMessage(err);
        setLoadError(message);
        if (!append) setPayments([]);
        toast.error(message);
      } finally {
        if (gen === fetchGenRef.current) {
          setLoading(false);
          setLoadingMore(false);
        }
      }
    },
    [needsSetup, itemsPerPage, dateFrom, dateTo]
  );

  useEffect(() => {
    if (needsSetup) {
      setPayments([]);
      setHasMore(false);
      setNextCursor(null);
      setLoadError(null);
      return;
    }
    loadPayments();
  }, [needsSetup, itemsPerPage, dateFrom, dateTo, loadPayments]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    if (!q) return payments;
    return payments.filter(
      (r) =>
        r.paymentMethod.toLowerCase().includes(q) ||
        r.status.toLowerCase().includes(q) ||
        r.receivedAt.toLowerCase().includes(q) ||
        formatMoney(r.amount, r.currency).toLowerCase().includes(q)
    );
  }, [payments, search]);

  const showListingEmpty = !loading && !loadError && payments.length === 0;
  const showFilteredEmpty = !loading && filtered.length === 0 && payments.length > 0;

  return (
    <div className="p-4 lg:p-6">
      <div className="flex flex-col w-full h-full gap-4">
        <PageHeader
          title={
            <span className="inline-flex items-center gap-2">
              Payments
              <BetaBadge />
            </span>
          }
          description={
            needsSetup
              ? "Connect Xonder to receive and track customer payments."
              : "Payments received via Xonder."
          }
          icon={CreditCard}
          variant="card"
          className="mb-0"
          mobileActionsInHeader={!needsSetup}
          actions={
            !needsSetup ? (
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="border-gray-200 text-gray-700 gap-1.5"
                  disabled={loading}
                  onClick={() => loadPayments()}
                >
                  <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
                  Refresh
                </Button>
              </div>
            ) : undefined
          }
        />

        {needsSetup ? (
          <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
            <PaymentsSetupEmpty onSetup={() => navigate("/payments/setup")} />
          </div>
        ) : (
          <>
            <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm space-y-4">
              <div className="flex flex-col lg:flex-row lg:items-end gap-4">
                <div className="flex flex-wrap items-end gap-3 flex-1">
                  <label className="block min-w-[140px]">
                    <span className="text-xs font-medium text-gray-500">From</span>
                    <input
                      type="date"
                      value={dateFrom}
                      onChange={(e) => setDateFrom(e.target.value)}
                      className="mt-1 w-full h-10 border border-gray-300 rounded-lg px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#885ABB]"
                    />
                  </label>
                  <label className="block min-w-[140px]">
                    <span className="text-xs font-medium text-gray-500">To</span>
                    <input
                      type="date"
                      value={dateTo}
                      onChange={(e) => setDateTo(e.target.value)}
                      className="mt-1 w-full h-10 border border-gray-300 rounded-lg px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#885ABB]"
                    />
                  </label>
                  {(dateFrom || dateTo) && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="text-gray-600 h-10"
                      onClick={() => {
                        setDateFrom("");
                        setDateTo("");
                      }}
                    >
                      Clear dates
                    </Button>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-500 font-medium whitespace-nowrap">Show</span>
                  <Select
                    value={itemsPerPage.toString()}
                    onValueChange={(value) => setItemsPerPage(Number(value))}
                  >
                    <SelectTrigger className="w-[80px] bg-white border-gray-200">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="10">10</SelectItem>
                      <SelectItem value="25">25</SelectItem>
                      <SelectItem value="50">50</SelectItem>
                    </SelectContent>
                  </Select>
                  <span className="text-sm text-gray-500 font-medium whitespace-nowrap">
                    per page
                  </span>
                </div>
              </div>
              <div className="relative w-full">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search method, amount, status, date…"
                  value={search}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)}
                  className="w-full h-10 border border-gray-300 rounded-lg pl-10 pr-4 text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#885ABB] focus:border-transparent"
                />
              </div>
            </div>

            <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden flex flex-col min-h-[320px]">
              {loading && payments.length === 0 ? (
                <div className="flex flex-col items-center justify-center min-h-[280px] text-gray-500 gap-3">
                  <Loader2 className="h-8 w-8 animate-spin text-[#885ABB]" />
                  <p className="text-sm">Loading payments…</p>
                </div>
              ) : loadError && payments.length === 0 ? (
                <div className="flex flex-col items-center justify-center min-h-[280px] px-6 text-center gap-4">
                  <p className="text-sm text-red-600">{loadError}</p>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => loadPayments()}
                    className="gap-2"
                  >
                    <RefreshCw className="h-4 w-4" />
                    Try again
                  </Button>
                </div>
              ) : showListingEmpty ? (
                <PaymentsReceivedEmpty />
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[520px] border-collapse">
                      <thead className="bg-[#885ABB] text-white text-sm font-bold">
                        <tr>
                          <th className="text-left px-6 py-3.5">Method</th>
                          <th className="text-left px-4 py-3.5">Amount</th>
                          <th className="text-left px-4 py-3.5">Status</th>
                          <th className="text-left px-6 py-3.5">Date</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100 text-sm">
                        {showFilteredEmpty ? (
                          <tr>
                            <td colSpan={4}>
                              <PaymentsNoResultsEmpty />
                            </td>
                          </tr>
                        ) : (
                          filtered.map((row) => (
                            <tr
                              key={row.id}
                              className="hover:bg-purple-50/40 transition-colors"
                            >
                              <td className="px-6 py-4 text-gray-700 capitalize">
                                {row.paymentMethod}
                              </td>
                              <td className="px-4 py-4 font-semibold text-[#885ABB] tabular-nums">
                                {formatMoney(row.amount, row.currency)}
                              </td>
                              <td className="px-4 py-4 text-gray-700">
                                <span
                                  className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${
                                    row.status.toLowerCase().includes("succeed") ||
                                      row.status.toLowerCase() === "paid"
                                      ? "bg-emerald-50 text-emerald-800"
                                      : row.status.toLowerCase().includes("fail") ||
                                          row.status.toLowerCase().includes("cancel")
                                        ? "bg-red-50 text-red-800"
                                        : "bg-gray-100 text-gray-700"
                                  }`}
                                >
                                  {row.status}
                                </span>
                              </td>
                              <td className="px-6 py-4 text-gray-600 whitespace-nowrap">
                                {formatPaymentDate(row.receivedAt)}
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>

                  {hasMore && !search.trim() && (
                    <div className="flex justify-end px-4 sm:px-6 py-3 border-t border-gray-200 bg-gray-50/80">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={loadingMore || !nextCursor}
                        className="gap-2"
                        onClick={() =>
                          nextCursor &&
                          loadPayments({ startingAfter: nextCursor, append: true })
                        }
                      >
                        {loadingMore ? (
                          <>
                            <Loader2 className="h-4 w-4 animate-spin" />
                            Loading…
                          </>
                        ) : (
                          "Load more"
                        )}
                      </Button>
                    </div>
                  )}
                </>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PaymentsListPage;
